import { useEffect, useCallback, useRef, useMemo, useState, type RefObject } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type * as mapboxgl from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';
import rainPathService, { type RainPathData } from '../../api/rainPathService';

dayjs.extend(utc);

interface UseRainPathLayerParams {
  mapRef: RefObject<mapboxgl.Map | null>;
  selectedEvent: EventWithCircuit | null;
  showRainPath: boolean;
  featureEnabled: boolean;
  selectedDateRange: { start: Date; end: Date } | null;
  currentTimestampUtc: string | null;
  styleVersion: number;
  onRainPathUpdate?: (data: RainPathData | null) => void;
}

const SOURCE_ID = 'rain-path-source';
const LAYER_IDS = [
  'rain-path-labels',
  'rain-path-ticks',
  'rain-path-dots',
  'rain-path-line',
];

const LINE_DURATION_MIN = 60;
const TICK_INTERVAL_MIN = 5;
const TICK_HALF_KM = 1.5;
const LINE_COLOR = '#D32F2F';

function projectPoint(
  lat: number, lon: number, bearingDeg: number, distKm: number,
): [number, number] {
  const R = 6371.0;
  const d = distKm / R;
  const brng = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lon * Math.PI) / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng),
  );
  const lon2 = lon1 + Math.atan2(
    Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
    Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
  );

  return [(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI];
}

export function useRainPathLayer({
  mapRef,
  selectedEvent,
  showRainPath,
  featureEnabled,
  selectedDateRange,
  currentTimestampUtc,
  styleVersion,
  onRainPathUpdate,
}: UseRainPathLayerParams): void {
  const [rainPathRecords, setRainPathRecords] = useState<RainPathData[]>([]);
  const lastFetchedRangeRef = useRef<string | null>(null);

  const isEnabled = featureEnabled && showRainPath && !!selectedEvent;

  const isMapStyleLoaded = useCallback((map: mapboxgl.Map): boolean => {
    try {
      if (!map?.style) return false;
      return map.isStyleLoaded();
    } catch {
      return false;
    }
  }, []);

  const cleanupLayers = useCallback((map: mapboxgl.Map) => {
    try {
      if (!isMapStyleLoaded(map)) return;
      LAYER_IDS.forEach(id => {
        try { if (map.getLayer(id)) map.removeLayer(id); } catch { /* */ }
      });
      try { if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID); } catch { /* */ }
    } catch { /* */ }
  }, [isMapStyleLoaded]);

  const buildGeoJSON = useCallback((
    rainPath: RainPathData,
  ): GeoJSON.FeatureCollection => {
    const features: GeoJSON.Feature[] = [];
    const { circuit, direction_deg, speed_kmh } = rainPath;

    if (!circuit || speed_kmh <= 0 || rainPath.drawable === false) {
      return { type: 'FeatureCollection', features };
    }

    const totalKm = speed_kmh * (LINE_DURATION_MIN / 60);
    const lineEnd = projectPoint(circuit.lat, circuit.lon, direction_deg, totalKm);

    features.push({
      type: 'Feature',
      properties: { type: 'rain-path-line' },
      geometry: {
        type: 'LineString',
        coordinates: [
          [circuit.lon, circuit.lat],
          lineEnd,
        ],
      },
    });

    const perpBearing = (direction_deg + 90) % 360;
    const kmPerTick = speed_kmh * (TICK_INTERVAL_MIN / 60);
    const numTicks = Math.floor(LINE_DURATION_MIN / TICK_INTERVAL_MIN);

    for (let i = 1; i <= numTicks; i++) {
      const distKm = i * kmPerTick;
      const timeMin = i * TICK_INTERVAL_MIN;
      const [tickLon, tickLat] = projectPoint(circuit.lat, circuit.lon, direction_deg, distKm);

      const tickLeft = projectPoint(tickLat, tickLon, perpBearing, TICK_HALF_KM);
      const tickRight = projectPoint(tickLat, tickLon, (perpBearing + 180) % 360, TICK_HALF_KM);

      features.push({
        type: 'Feature',
        properties: { type: 'rain-path-tick' },
        geometry: {
          type: 'LineString',
          coordinates: [tickLeft, tickRight],
        },
      });

      features.push({
        type: 'Feature',
        properties: {
          type: 'rain-path-dot',
          label: `${timeMin}'`,
        },
        geometry: {
          type: 'Point',
          coordinates: [tickLon, tickLat],
        },
      });
    }

    return { type: 'FeatureCollection', features };
  }, []);

  const updateLayers = useCallback((
    map: mapboxgl.Map,
    rainPath: RainPathData,
  ) => {
    if (!isMapStyleLoaded(map)) return;

    try {
      const geojson = buildGeoJSON(rainPath);

      const existingSource = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;
      if (existingSource) {
        existingSource.setData(geojson);
        return;
      }

      map.addSource(SOURCE_ID, { type: 'geojson', data: geojson });

      map.addLayer({
        id: 'rain-path-line',
        type: 'line',
        source: SOURCE_ID,
        filter: ['==', ['get', 'type'], 'rain-path-line'],
        paint: {
          'line-color': LINE_COLOR,
          'line-width': 2.5,
          'line-opacity': 0.85,
        },
      });

      map.addLayer({
        id: 'rain-path-ticks',
        type: 'line',
        source: SOURCE_ID,
        filter: ['==', ['get', 'type'], 'rain-path-tick'],
        paint: {
          'line-color': LINE_COLOR,
          'line-width': 2,
          'line-opacity': 0.8,
        },
      });

      map.addLayer({
        id: 'rain-path-dots',
        type: 'circle',
        source: SOURCE_ID,
        filter: ['==', ['get', 'type'], 'rain-path-dot'],
        paint: {
          'circle-radius': 4,
          'circle-color': LINE_COLOR,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#FFFFFF',
          'circle-opacity': 0.9,
        },
      });

      map.addLayer({
        id: 'rain-path-labels',
        type: 'symbol',
        source: SOURCE_ID,
        filter: ['==', ['get', 'type'], 'rain-path-dot'],
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 10,
          'text-offset': [0, 1.6],
          'text-anchor': 'top',
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#263238',
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 1.5,
        },
      });
    } catch (e) {
      console.warn('[RainPathLayer] Failed to update layers:', e);
    }
  }, [isMapStyleLoaded, buildGeoJSON]);

  // Fetch rain path data when date range changes
  useEffect(() => {
    if (!isEnabled || !selectedDateRange) {
      setRainPathRecords([]);
      lastFetchedRangeRef.current = null;
      return;
    }

    const startTs = dayjs(selectedDateRange.start).utc().format('YYYYMMDDHHmmss');
    const endTs = dayjs(selectedDateRange.end).utc().format('YYYYMMDDHHmmss');
    const rangeKey = `${startTs}-${endTs}`;

    if (lastFetchedRangeRef.current === rangeKey) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        const records = await rainPathService.getRainPathRange({
          start_timestamp: startTs,
          end_timestamp: endTs,
        });
        if (cancelled) return;
        setRainPathRecords(records);
        lastFetchedRangeRef.current = rangeKey;
      } catch (error) {
        console.error('[RainPathLayer] Fetch failed:', error);
        setRainPathRecords([]);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [isEnabled, selectedDateRange]);

  // Find the record closest to the current playback timestamp
  const currentRainPath = useMemo(() => {
    if (rainPathRecords.length === 0 || !currentTimestampUtc) return null;

    const currentTs = currentTimestampUtc;

    let closest: RainPathData | null = null;
    let closestDiff = Infinity;

    for (const record of rainPathRecords) {
      const recordTs = dayjs(record.timestamp).utc().format('YYYYMMDDHHmmss');
      if (recordTs <= currentTs) {
        const diff = parseInt(currentTs, 10) - parseInt(recordTs, 10);
        if (diff < closestDiff) {
          closestDiff = diff;
          closest = record;
        }
      }
    }

    return closest;
  }, [rainPathRecords, currentTimestampUtc]);

  // Update map visualization
  useEffect(() => {
    const map = mapRef.current;

    if (!map || !isEnabled) {
      if (map && isMapStyleLoaded(map)) cleanupLayers(map);
      onRainPathUpdate?.(null);
      return;
    }

    onRainPathUpdate?.(currentRainPath);

    let cancelled = false;

    const update = () => {
      if (cancelled) return;
      if (!isMapStyleLoaded(map)) {
        setTimeout(update, 100);
        return;
      }

      if (currentRainPath && currentRainPath.speed_kmh > 0 && currentRainPath.drawable !== false) {
        updateLayers(map, currentRainPath);
      } else {
        cleanupLayers(map);
      }
    };

    update();
    return () => { cancelled = true; };
  }, [
    mapRef,
    isEnabled,
    currentRainPath,
    styleVersion,
    cleanupLayers,
    updateLayers,
    onRainPathUpdate,
    isMapStyleLoaded,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    const map = mapRef.current;
    return () => {
      if (map && isMapStyleLoaded(map)) cleanupLayers(map);
    };
  }, [mapRef, cleanupLayers, isMapStyleLoaded]);
}
